<template>
  <div
    class="tw-mb-4 tw-inline-flex tw-flex-col tw-bg-white tw-p-6 tw-rounded-lg tw-border tw-border-primary-grey-100 tw-w-full"
  >
    <div class="tw-flex tw-items-center tw-mb-4">
      <div
        class="tw-mr-4 tw-p-4 tw-bg-primary-grey-50 tw-inline-flex tw-justify-center tw-items-center tw-rounded-lg tw-self-start"
      >
        <cb-icon-contract class="tw-w-8 tw-h-8" />
      </div>
      <div class="tw-flex tw-flex-col">
        <p class="tw-text-lg tw-font-bold tw-mr-4">
          {{ t("ndr-email-address-title") }}
        </p>
        <p>
          {{ t("ndr-email-address-value") }}
        </p>
      </div>
      <button
        class="tw-flex tw-items-center tw-justify-center tw-ml-auto tw-mt-2"
        @click="isExpanded = !isExpanded"
      >
        <cb-icon-chevron-down
          class="tw-w-6 tw-h-6 tw-transition-transform tw-duration-200"
          :class="{ 'tw-rotate-180': isExpanded }"
        />
      </button>
    </div>
    <div v-if="isConnected" class="tw-flex tw-justify-between">
      <div class="tw-flex tw-items-center">
        <span class="tw-text-sm tw-text-primary-grey-300 tw-mr-8">{{
          t("status")
        }}</span>
        <div
          class="tw-w-3 tw-h-3 tw-rounded-full tw-mr-2"
          :class="
            isConnected ? 'tw-bg-system-success-500' : 'tw-bg-system-error-500'
          "
        ></div>
        <span class="tw-text-sm">{{
          isConnected ? t("active") : t("not-active")
        }}</span>
      </div>
      <p class="tw-text-xs tw-text-primary-grey-300">
        {{ t("last-update") }} {{ lastUpdate }}
      </p>
    </div>
    <div
      v-show="isExpanded"
      class="tw-pt-4 tw-mt-4 tw-border-t tw-border-primary-grey-100"
    >
      <div
        v-if="hasError"
        class="tw-bg-system-error-50 tw-p-4 tw-rounded-lg tw-mb-4"
      >
        <div class="tw-flex tw-items-center tw-gap-2 tw-mb-4">
          <TErrorIcon />
          <p>{{ t("connection-error-title") }}</p>
        </div>
        <p class="tw-text-sm tw-mb-4">
          {{ t("connection-error-error") }}
        </p>
        <p v-if="errorMessage" class="tw-text-sm tw-mb-4 tw-break-words">
          {{ errorMessage }}
        </p>
        <div class="tw-flex tw-justify-end tw-gap-4">
          <cl-button @on-click="isSlideoutOpen = true">
            {{ t("manually-add-ndr") }}
          </cl-button>
          <authorisation-wrapper
            :resource="ADMINISTRATORS"
            :action="WRITE"
            v-slot="{ isAllowed: isWriteAllowed }"
          >
            <cl-button
              variant="secondary"
              @on-click="$emit('connect')"
              :disabled="isRequestPending"
              :readonly="!isWriteAllowed"
            >
              <span v-if="isRequestPending">
                <cl-spinner
                  :loading-text="$t('loading')"
                  :size="'small'"
                  class="tw-mr-2"
                />
                <span>{{ t("creating-ndr") }}</span>
              </span>
              <span v-else>{{ t("try-again") }}</span>
            </cl-button>
          </authorisation-wrapper>
        </div>
      </div>
      <div class="tw-grid tw-grid-cols-3 tw-gap-4">
        <div class="tw-col-span-1">
          <p class="tw-text-lg tw-font-medium tw-mb-4">
            {{ t("how-does-it-work") }}
          </p>
          <p class="tw-font-normal tw-mb-2">
            {{ t("ndr-definition") }}
          </p>
          <t-link
            :href="'https://support.titanhq.com/en/62976-add-a-non-delivery-report-email-address.html'"
            :is-external="true"
            :string="t('learn-more')"
            data-test-id="m365-contact-support-link"
            class="tw-underline"
          />
        </div>
        <div class="tw-col-span-2">
          <div class="tw-mb-4">
            <p
              class="tw-p-4 tw-rounded-xl tw-border tw-border-primary-grey-100 tw-text-primary-grey-300 tw-bg-primary-grey-25"
            >
              {{ customerName }}
            </p>
            <p class="tw-text-sm tw-text-primary-grey-500">
              {{ t("connections-tenant-name") }}
            </p>
          </div>
          <div class="tw-bg-primary-cobalt-blue-25 tw-p-4 tw-rounded-xl">
            <p class="tw-mb-4">{{ t("what-will-happen") }}</p>
            <ul class="tw-pl-6 tw-list-disc">
              <li class="tw-text-sm tw-mb-2" v-html="t('ndr-1')"></li>
              <li class="tw-text-sm tw-mb-2" v-html="t('ndr-2')"></li>
              <li class="tw-text-sm tw-mb-2" v-html="t('ndr-3')"></li>
            </ul>
          </div>
        </div>
      </div>
      <div v-if="isConnected" class="tw-flex tw-justify-end tw-items-center">
        <cl-button
          variant="secondary"
          :href="M365_ACCOUNT_LINK"
          @click="navigateToLink"
        >
          {{ t("open-microsoft-ndr-settings") }}
          <cb-icon-external-link :width="20" />
        </cl-button>
      </div>
      <div v-else class="tw-flex tw-justify-end tw-items-center">
        <cl-button
          v-if="isM365Connected"
          variant="link-secondary"
          data-test-id="m365-contact-support-link"
          class="tw-underline tw-mr-4"
          @on-click="isSlideoutOpen = true"
          >{{ t("not-working") }}</cl-button
        >
        <authorisation-wrapper
          :resource="ADMINISTRATORS"
          :action="WRITE"
          v-slot="{ isAllowed: isWriteAllowed }"
        >
          <cl-button
            variant="secondary"
            @on-click="$emit('connect')"
            :disabled="isRequestPending || !isM365Connected"
            :readonly="!isWriteAllowed"
          >
            <span v-if="!isM365Connected">M365 Connection needed</span>
            <span v-else-if="isRequestPending">
              <cl-spinner
                :loading-text="$t('loading')"
                :size="'small'"
                class="tw-mr-2"
              />
              <span>{{ t("creating-ndr") }}</span>
            </span>
            <span v-else>{{ t("create-ndr") }}</span>
          </cl-button>
        </authorisation-wrapper>
      </div>
    </div>
  </div>
  <ndr-connection-slideout v-model="isSlideoutOpen" />
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import TLink from "@/components/TLink";
import NdrConnectionSlideout from "./ndr-connection-slideout/ndr-connection-slideout.vue";
import TErrorIcon from "@/components/TErrorIcon/TErrorIcon.vue";
import { AuthorisationWrapper, ADMINISTRATORS, WRITE } from "@/authorisation";

interface NdrConnectionCardProps {
  isConnected: boolean;
  isM365Connected: boolean;
  lastUpdate: string;
  isRequestPending: boolean;
  customerName: string;
  hasError: boolean;
  errorMessage?: string;
}

const { t } = useI18n();
const M365_ACCOUNT_LINK =
  "https://purview.microsoft.com/settings/application-settings/datalifecyclemanagement";

const props = defineProps<NdrConnectionCardProps>();
const isExpanded = ref(props.isM365Connected && !props.isConnected);
const isSlideoutOpen = ref<boolean>(false);

watch(
  [() => props.isConnected, () => props.isM365Connected],
  ([ndrConnected, m365Connected]) => {
    isExpanded.value = m365Connected && !ndrConnected;
  },
);
const navigateToLink = () => {
  window.open(M365_ACCOUNT_LINK, "_blank");
};

defineEmits<{
  connect: [];
}>();
</script>
